// pages/FormViewerPage/services/formViewerService/shipping.ts — Shipping & COD fee computation
// Arsitektur 2026-08-14: Everpro = satu-satunya provider. Flat ongkir & Biteship dihentikan.
import type { Form, ShippingSettings, PaymentSettings, ShippingSetting } from '../../../../types';
import type { EverproRate } from '../../../../services/everproAPI';
import { getEffectiveMaxSubsidy } from '../../../../utils/shippingSubsidy';

// Dipakai validasi COD (effectsForm) & zone — bukan untuk hitung ongkir.
export function getFlatRegionKey(provinceName: string): 'flat_jawa' | 'flat_bali' | 'flat_sumatra1' | 'flat_sumatra2' | null {
    const p = provinceName.toLowerCase();
    const EXCLUDED = ['aceh', 'kepulauan riau', 'bangka belitung'];
    if (EXCLUDED.some(e => p.includes(e))) return null;
    const jawa = ['banten', 'daerah khusus ibukota jakarta', 'dki jakarta', 'jakarta', 'jawa barat', 'jawa tengah', 'di yogyakarta', 'daerah istimewa yogyakarta', 'jawa timur'];
    if (jawa.some(j => p.includes(j))) return 'flat_jawa';
    if (p.includes('bali')) return 'flat_bali';
    const sumatra1 = ['lampung', 'sumatera selatan', 'sumatra selatan', 'sumsel', 'palembang', 'jambi', 'sumatera utara', 'sumut', 'medan', 'riau', 'pekanbaru'];
    if (sumatra1.some(s => p.includes(s))) return 'flat_sumatra1';
    const sumatra2 = ['bengkulu', 'sumatera barat', 'sumbar', 'padang'];
    if (sumatra2.some(s => p.includes(s))) return 'flat_sumatra2';
    return null;
}

export function computeShippingCost(
    form: Form | null,
    selectedShippingKey: keyof ShippingSettings | undefined,
    selectedEverproRate: EverproRate | null,
    _selectedBiteshipRate: any,
    _addressData: any,
    currentCombination?: { maxSubsidy?: number | null; attributes?: Record<string, string> } | null,
    quantity?: number | null,
): number {
    if (!form) return 0;
    if (selectedEverproRate) {
        // Subsidi gratis-ongkir-maks: bayar = max(0, tarif - maks). Berlaku hanya saat
        // kebijakan form = subsidi (free.visible && !regular.visible && maks > 0).
        // Level varian: maks ikut varian terpilih (null = ikut induk).
        const maks = getEffectiveMaxSubsidy(form, currentCombination ?? null, quantity ?? 1);
        const price = selectedEverproRate.price || 0;
        if (maks > 0 && form.shippingSettings?.free?.visible && !form.shippingSettings?.regular?.visible) {
            return Math.max(0, price - Math.round(maks));
        }
        return price;
    }
    const sk = selectedShippingKey;
    if (!sk || sk === 'free') return 0;
    // Flat per-pulau DIHAPUS 2026-10-10 — semua ongkir = tarif real agregator.
    // Metode lama 'flat'/'flat_*' yang tersimpan di order lama dianggap 0 di sini;
    // ongkir real dihitung server-side (autoOngkirEverpro) setelah alamat dianalisis.
    const s = form.shippingSettings?.[sk] as ShippingSetting;
    if (!s) return 0;
    // 'regular' = kebijakan "Bayar via Everpro" (BUKAN tarif tetap). Legacy regular.cost
    // DIHAPUS 2026-09-24 (sumber fallback Rp10.000) → jangan kembalikan cost-nya. Untuk mode
    // sederhana ongkir dihitung server-side (Everpro) setelah alamat dianalisis.
    if (String(sk) === 'regular') return 0;
    return s.cost || 0;
}

export function computeCodFee(
    form: Form | null,
    selectedPaymentKey: keyof PaymentSettings | undefined,
    currentCombination: any,
    shippingCost: number,
    quantity: number = 1
): number {
    if (!form) return 0;
    const pk = selectedPaymentKey;
    if (!pk) return 0;
    const p = form.paymentSettings?.[pk] as any;
    const isCodPayment = ['cod', 'codJawaBali', 'codLuarJawaBali'].includes(pk as string);
    if (!isCodPayment || !p?.visible) return 0;
    const unitPrice = currentCombination?.sellingPrice || form.productPrice || 0;
    const lineSubtotal = unitPrice * (quantity || 1);
    // handlingFeeBase menentukan basis hitung: 'product' = subtotal produk saja,
    // 'product_and_shipping' (default) = subtotal + ongkir. Sebelumnya field ini
    // diabaikan di sini (walau sudah dipakai di preview UI PaymentMethodSection).
    const base = p.handlingFeeBase === 'product' ? lineSubtotal : lineSubtotal + shippingCost;
    return Math.round(base * (p.handlingFeePercentage || 0) / 100);
}
